import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { GraficoProcesadosPorMotivo } from "./GraficosHistorial"
import TablaImportacion from "./TablaImportacion"
import {
  TarjetaNoImportados,
  TarjetaNumero,
  desglosarNoImportadas,
} from "./TarjetasHistorial"
import { clasificarResultado } from "./migracionEstadoHelpers"
import {
  datosGraficoActividad,
  prepararFilasActividad,
  resumenStockAgricola,
} from "./preparacionActividades"
import {
  opcionesEntidadFiltro,
  opcionesResultadoFiltro,
} from "./preparacionHistorial"

// BDSJEA-441/443: secciones Siembras, Ganadería y Tambo del Historial. Mismo molde
// que Establecimientos: gráfico de torta por categoría cerrada + «Otros
// motivos», tarjetas y tablas de procesados / no importados. Solo muestran
// columnas de la lista blanca (ids, categorías cerradas): jamás razón social,
// CUIT, producto ni clave_origen.

const ID_ORIGEN = {
  key: "id_establecimiento_origen",
  label: "ID origen",
  tipo: "id",
}
const ID_CLIENTE = { key: "id_cliente", label: "ID cliente", tipo: "id" }

const COLUMNAS_BASE = nivel => [
  { key: "id_plan", label: "ID plan", tipo: "id" },
  nivel === "siembras" ? ID_ORIGEN : ID_CLIENTE,
  { key: "id_productor", label: "ID productor", tipo: "id" },
  { key: "id_establecimiento", label: "ID establecimiento", tipo: "id" },
  { key: "entidad", label: "Entidad", tipo: "texto" },
  { key: "resultado_etiqueta", label: "Resultado", tipo: "texto" },
  { key: "motivo", label: "Motivo", tipo: "motivo", contexto: nivel },
  { key: "avisos_etiqueta", label: "Avisos", tipo: "texto" },
]

const COLUMNAS = Object.freeze({
  siembras: COLUMNAS_BASE("siembras"),
  ganaderia: COLUMNAS_BASE("ganaderia"),
  tambo: COLUMNAS_BASE("tambo"),
  stockAgricola: [
    ...COLUMNAS_BASE("stockAgricola").slice(0, 3),
    { key: "columna_origen", label: "Columna de origen", tipo: "texto" },
    { key: "id_establecimiento", label: "ID establecimiento", tipo: "id" },
    ...COLUMNAS_BASE("stockAgricola").slice(4),
  ],
})

const BUSCAR = Object.freeze({
  siembras: ["id_plan", "id_establecimiento_origen", "motivo"],
  ganaderia: ["id_plan", "id_cliente", "motivo"],
  tambo: ["id_plan", "id_cliente", "motivo"],
  stockAgricola: ["id_plan", "id_cliente", "motivo"],
})

const TITULOS = Object.freeze({
  siembras: { titulo: "Siembras", icono: "mdi-sprout" },
  ganaderia: { titulo: "Ganadería", icono: "mdi-cow" },
  tambo: { titulo: "Tambo", icono: "mdi-water" },
  stockAgricola: { titulo: "Stock agrícola", icono: "mdi-warehouse" },
})

const PREFIJO = Object.freeze({
  siembras: "sie",
  ganaderia: "gan",
  tambo: "tam",
  stockAgricola: "stk",
})

const filtros = (filas, mapaEntidades) => [
  {
    key: "id_entidad",
    label: "Entidad",
    todasLabel: "Todas las entidades",
    opciones: opcionesEntidadFiltro(filas, mapaEntidades),
  },
  {
    key: "resultado",
    label: "Resultado",
    todasLabel: "Todos los resultados",
    opciones: opcionesResultadoFiltro(filas),
  },
]

const SeccionActividad = ({ nivel, filas, mapaEntidades, noEjecutado }) => {
  const { titulo, icono } = TITULOS[nivel]
  const p = PREFIJO[nivel]
  const codigosEntidad = useMemo(
    () => Object.values(mapaEntidades || {}),
    [mapaEntidades],
  )
  const procesados = useMemo(
    () => prepararFilasActividad(filas, nivel, mapaEntidades),
    [filas, nivel, mapaEntidades],
  )
  const grafico = useMemo(
    () => datosGraficoActividad(filas, nivel),
    [filas, nivel],
  )
  const noImportadas = useMemo(
    () =>
      procesados.filter(
        f => clasificarResultado(f.resultado, nivel).noImportado,
      ),
    [procesados, nivel],
  )
  const conteo = useMemo(() => {
    const por = r => procesados.filter(f => f.resultado === r).length
    return {
      creados: por("CREADO"),
      actualizados: por("ACTUALIZADO"),
      yaExistian: procesados.filter(
        f =>
          f.resultado === "SALTADO_EXISTENTE" || f.resultado === "YA_EXISTIA",
      ).length,
      historia: por("CREADO_HISTORIA"),
    }
  }, [procesados])
  const desglose = useMemo(
    () => desglosarNoImportadas(noImportadas),
    [noImportadas],
  )
  const stock = useMemo(
    () => (nivel === "stockAgricola" ? resumenStockAgricola(filas) : null),
    [filas, nivel],
  )
  const filtrosProcesados = useMemo(
    () => filtros(procesados, mapaEntidades),
    [procesados, mapaEntidades],
  )
  const filtrosNoImportados = useMemo(
    () => filtros(noImportadas, mapaEntidades),
    [noImportadas, mapaEntidades],
  )

  if (noEjecutado) {
    return (
      <Card className="mb-3" data-testid={`seccion-${nivel}`}>
        <CardBody>
          <CardTitle tag="h6">
            <i className={`mdi ${icono} me-2`} />
            {titulo}
          </CardTitle>
          <p className="text-muted mb-0" data-testid={`${p}-no-ejecutado`}>
            <i className="mdi mdi-circle-off-outline me-1" aria-hidden="true" />
            No ejecutado: este nivel no se importó en esta corrida.
          </p>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="mb-3" data-testid={`seccion-${nivel}`}>
      <CardBody>
        <CardTitle tag="h6">
          <i className={`mdi ${icono} me-2`} />
          {titulo}
        </CardTitle>
        <Row className="mb-3 align-items-center">
          <Col lg={4} className="mb-3 mb-lg-0">
            <GraficoProcesadosPorMotivo
              titulo="Procesados por motivo"
              total={grafico.total}
              creados={grafico.creados}
              noImportados={grafico.noImportados}
              motivos={grafico.motivos}
              testId={`grafico-${nivel}`}
            />
          </Col>
          <Col lg={8}>
            <Row className="align-items-start">
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Total"
                  valor={procesados.length}
                  color="secondary"
                  testId={`${p}-total`}
                />
              </Col>
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta={nivel === "tambo" ? "Creados" : "Creadas"}
                  valor={conteo.creados + conteo.historia}
                  color="success"
                  testId={`${p}-creados`}
                />
              </Col>
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Actualizados"
                  valor={conteo.actualizados}
                  color="primary"
                  testId={`${p}-actualizados`}
                />
              </Col>
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Ya existían"
                  valor={conteo.yaExistian}
                  color="warning"
                  testId={`${p}-ya-existian`}
                />
              </Col>
              <Col xs={12} className="mt-2">
                <TarjetaNoImportados
                  total={noImportadas.length}
                  desglose={desglose}
                  testId={`${p}-no-importados`}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        {stock && (
          <Row className="mb-3" data-testid="stk-resumen">
            <Col xs={6} md={3}>
              <TarjetaNumero
                etiqueta="QQ importados"
                valor={stock.qqImportados}
                decimales={2}
                color="success"
                testId="stk-qq-importados"
              />
            </Col>
            <Col xs={6} md={3}>
              <TarjetaNumero
                etiqueta="QQ excluidos"
                valor={stock.qqExcluidos}
                decimales={2}
                color="danger"
                testId="stk-qq-excluidos"
              />
            </Col>
            <Col xs={6} md={3}>
              <TarjetaNumero
                etiqueta="Valuación total (USD)"
                valor={stock.valuacionTotal}
                decimales={2}
                color="primary"
                testId="stk-valuacion"
              />
            </Col>
            <Col xs={12} md={3} className="small text-muted">
              <div data-testid="stk-origen-precio">
                Precio de valor_agro: zona del departamento{" "}
                {stock.origenPrecio.ZONA_DEPARTAMENTO}, zona por defecto{" "}
                {stock.origenPrecio.ZONA_PROVINCIA}, promedio entre zonas{" "}
                {stock.origenPrecio.PROMEDIO_ENTRE_ZONAS}, sin parametría{" "}
                {stock.origenPrecio.SIN_PARAMETRIA}
              </div>
            </Col>
          </Row>
        )}
        <Row>
          <Col className="mb-3">
            <p className="text-muted small mb-1">Procesados</p>
            <TablaImportacion
              testId={`${p}-tabla-procesados`}
              titulo={`${titulo} procesados`}
              maximizable
              codigosEntidad={codigosEntidad}
              columnas={COLUMNAS[nivel]}
              filas={procesados}
              buscarEn={BUSCAR[nivel]}
              placeholderBuscador="Buscar por ID o motivo"
              filtros={filtrosProcesados}
              textoVacio={`No hay filas de ${titulo.toLowerCase()} procesadas.`}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <p className="text-muted small mb-1">No importados</p>
            <TablaImportacion
              testId={`${p}-tabla-no-importados`}
              titulo={`${titulo} no importados`}
              maximizable
              codigosEntidad={codigosEntidad}
              columnas={COLUMNAS[nivel]}
              filas={noImportadas}
              buscarEn={BUSCAR[nivel]}
              placeholderBuscador="Buscar por ID o motivo"
              filtros={filtrosNoImportados}
              textoVacio={`No hay filas de ${titulo.toLowerCase()} sin importar.`}
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

SeccionActividad.propTypes = {
  nivel: PropTypes.oneOf(["siembras", "ganaderia", "tambo", "stockAgricola"])
    .isRequired,
  filas: PropTypes.array,
  mapaEntidades: PropTypes.object,
  noEjecutado: PropTypes.bool,
}

export default SeccionActividad
